import {
  Button,
  CircularProgress,
  lighten,
  ListItem,
  ListItemText,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Check as CheckIcon, Close, Edit } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import dayjs from "dayjs";
import { omit } from "lodash";
import React, { useEffect, useState } from "react";
import api from "../../api";
import InlineEditable from "../../components/InlineEditable";
import { stripPhoneSpecialCharacters } from "../../modules/displayUtils";

export const Check = ({
  kybVerificationId,
  check: originalCheck,
  setKybVerification,
  kybVerification,
}) => {
  const classes = useStyles();

  const [check, setCheck] = useState(originalCheck);
  const [otherValue, setOtherValue] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    setCheck(originalCheck);
  }, [originalCheck]);

  const respondCheck = async (status, selected) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.postKybVerificationUpdateCheck(kybVerificationId, {
        ...check,
        status,
        selected: status === "approved" ? selected : null,
      });
      setKybVerification(result.data);
    } catch (err) {
      const errorMessage = err?.response?.data?.error?.message || err.message;
      setError(errorMessage);
    }
    setLoading(false);
    setEditMode(false);
  };

  const getInlineEditableProps = (checkValue, value) => {
    switch (check.type) {
      case "date":
        return {
          value: value === "" ? undefined : value,
          inputType: "date",
          onCommit: (value) =>
            setOtherValue(value ? dayjs(value).format("YYYY-MM-DD") : value),
        };
      case "ssn":
        return {
          inputType: "ssn",
        };
      case "phone":
        return {
          inputType: "phone",
          displayValue: stripPhoneSpecialCharacters(value),
        };
      case "number":
        return {
          type: "number",
          onCommit: (value) => {
            const numberValue = parseFloat(value);
            setOtherValue((prev) => (isNaN(numberValue) ? prev.value : numberValue));
          },
        };
      default:
        return {};
    }
  };

  const renderOtherInput = (checkValue) => {
    const value =
      otherValue ||
      (originalCheck.selected?.source === "other" && originalCheck.selected?.value) ||
      checkValue.value;
    return (
      <InlineEditable
        editable={kybVerification.status === "ready_to_review" && (!check.status || editMode)}
        value={value}
        label={checkValue.title}
        onCommit={(value) => setOtherValue(value)}
        {...getInlineEditableProps(checkValue, value)}
      />
    );
  };

  return (
    <ListItem className={classes.checkInput}>
      <div>
        <div className={classes.checkTitleContainer}>
          {check.status && (
            <>
              {check.status === "approved" ? (
                <CheckIcon className={classes.approvedCheck} />
              ) : (
                <Close color="error" />
              )}
            </>
          )}
          <Typography className={classes.checkTitle}>{check.title}</Typography>
          {(kybVerification.status === "ready_to_review" && (!check.status || editMode)) ? (
            <Button
              className={classes.redButton}
              variant="outlined"
              onClick={() => respondCheck("rejected")}
            >
              <Close />
            </Button>
          ) : null}
          {check.status && !editMode && kybVerification.status === "ready_to_review" ? (
            <Button onClick={() => setEditMode(true)}>
              <Edit />
            </Button>
          ) : null}
        </div>
        <Typography className={classes.checkHint}>{check.hint}</Typography>

        {check.values.map((checkValue) => (
          <div
            className={`${classes.checkValueContainer} ${
              checkValue.source === originalCheck.selected?.source && !editMode
                ? classes.selectedCheck
                : ""
            }`}
            key={checkValue.source}
          >
            {checkValue.source === "other" ? (
              renderOtherInput(checkValue)
            ) : (
              <ListItem>
                <ListItemText>
                  {checkValue.title && <span dangerouslySetInnerHTML={{__html: checkValue.title + ":&nbsp;" }}></span>}
                  <span className={classes.bold}>{checkValue.value}</span>
                </ListItemText>
              </ListItem>
            )}

            {(kybVerification.status === "ready_to_review" && (!check.status || editMode)) ? (
              <div className={classes.checkActionButtonContainer}>
                <Button
                  disabled={checkValue.source === "other" && !otherValue}
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    respondCheck(
                      "approved",
                      omit(
                        checkValue.source === "other"
                          ? { ...checkValue, value: otherValue }
                          : checkValue,
                        ["title"]
                      )
                    )
                  }
                  className={classes.greenButton}
                >
                  <CheckIcon fontSize="small" />
                </Button>
              </div>
            ) : null}
          </div>
        ))}
        {loading ? <CircularProgress /> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
      </div>
    </ListItem>
  );
};

const useStyles = makeStyles((theme) => ({
  checkInput: {
    marginBottom: 4,
  },
  checkActionButtonContainer: {
    display: "flex",
    marginLeft: 16,
  },
  checkValueContainer: {
    display: "flex",
    alignItems: "center",
  },
  bold: {
    fontWeight: "bold",
  },
  checkTitleContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: 4,
  },
  checkTitle: {
    fontWeight: "bold",
    marginRight: 16,
    borderBottom: "1px solid black",
  },
  checkHint: {
    fontStyle: "italic",
  },
  selectedCheck: {
    backgroundColor: lighten(theme.palette.success.light, 0.5),
  },
  approvedCheck: {
    color: theme.palette.success.light,
  },
  greenButton: {
    borderColor: theme.palette.success.light,
    color: theme.palette.success.light,
  },
  redButton: {
    borderColor: theme.palette.error.light,
    color: theme.palette.error.light,
  },
}));
